import React, { useState, useEffect } from 'react';
import moment from 'moment';

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar',
  'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep',
  'Oct', 'Nov', 'Dec',
];

export default function MonthPickerModal({
  isOpen,
  onClose,
  onConfirm,
  initialStartDate,
  initialEndDate,
  activeField = 'from',
}) {
  const currentYear = moment().year();
  const currentMonthIdx = moment().month(); // 0-indexed

  // Parse initial start and end moments
  const startMoment = initialStartDate ? moment(initialStartDate) : moment().startOf('month');
  const endMoment = initialEndDate ? moment(initialEndDate) : moment().endOf('month');

  const [fromYear, setFromYear] = useState(startMoment.year());
  const [fromMonth, setFromMonth] = useState(startMoment.month());
  const [toYear, setToYear] = useState(endMoment.year());
  const [toMonth, setToMonth] = useState(endMoment.month());
  const [viewYear, setViewYear] = useState(
    activeField === 'from' ? startMoment.year() : endMoment.year()
  );

  useEffect(() => {
    if (isOpen) {
      const sM = initialStartDate ? moment(initialStartDate) : moment().startOf('month');
      const eM = initialEndDate ? moment(initialEndDate) : moment().endOf('month');
      setFromYear(sM.year());
      setFromMonth(sM.month());
      setToYear(eM.year());
      setToMonth(eM.month());
      setViewYear(activeField === 'from' ? sM.year() : eM.year());
    }
  }, [isOpen, initialStartDate, initialEndDate, activeField]);

  if (!isOpen) return null;

  const handlePrevYear = () => setViewYear((y) => y - 1);
  const handleNextYear = () => {
    if (viewYear < currentYear) setViewYear((y) => y + 1);
  };

  const handleMonthClick = (mIdx) => {
    // Disable future months
    if (viewYear === currentYear && mIdx > currentMonthIdx) return;
    if (viewYear > currentYear) return;

    if (activeField === 'from') {
      const newFrom = moment().year(viewYear).month(mIdx).startOf('month');
      const currentTo = moment().year(toYear).month(toMonth).endOf('month');

      setFromYear(viewYear);
      setFromMonth(mIdx);

      // If new From is after current To, adjust To to match From
      if (newFrom.isAfter(currentTo)) {
        setToYear(viewYear);
        setToMonth(mIdx);
      }
    } else {
      // activeField === 'to'
      const newTo = moment().year(viewYear).month(mIdx).endOf('month');
      const currentFrom = moment().year(fromYear).month(fromMonth).startOf('month');

      setToYear(viewYear);
      setToMonth(mIdx);

      // If new To is before current From, adjust From to match To
      if (newTo.isBefore(currentFrom)) {
        setFromYear(viewYear);
        setFromMonth(mIdx);
      }
    }
  };

  const handleConfirm = () => {
    const finalStart = moment()
      .year(fromYear)
      .month(fromMonth)
      .startOf('month')
      .format('YYYY-MM-DD');

    const finalEnd = moment()
      .year(toYear)
      .month(toMonth)
      .endOf('month')
      .format('YYYY-MM-DD');

    onConfirm({
      startDate: finalStart,
      endDate: finalEnd,
      label:
        fromYear === toYear && fromMonth === toMonth
          ? `${MONTH_NAMES[fromMonth]} ${fromYear}`
          : `${MONTH_NAMES[fromMonth]} ${fromYear} - ${MONTH_NAMES[toMonth]} ${toYear}`,
    });
    onClose();
  };

  const rangeStart = moment().year(fromYear).month(fromMonth).startOf('month');
  const rangeEnd = moment().year(toYear).month(toMonth).endOf('month');
  const isSingle = rangeStart.isSame(rangeEnd, 'month');

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.45)',
        backdropFilter: 'blur(2px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: '16px',
          boxShadow: '0 20px 40px rgba(11, 46, 69, 0.22), 0 0 0 1px rgba(0,0,0,0.06)',
          width: '100%',
          maxWidth: '320px',
          overflow: 'hidden',
          animation: 'popIn 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with Field Label & Year Navigation */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '18px 20px 10px',
          }}
        >
          <button
            type="button"
            onClick={handlePrevYear}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#8aa1ad',
              display: 'grid',
              placeItems: 'center',
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              transition: 'background 0.15s, color 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f1f5f9';
              e.currentTarget.style.color = '#2b3c73';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none';
              e.currentTarget.style.color = '#8aa1ad';
            }}
            title="Previous Year"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <span
              style={{
                fontSize: '18px',
                fontWeight: '800',
                color: '#2b3c73',
                letterSpacing: '-0.01em',
              }}
            >
              {viewYear}
            </span>
            <span
              style={{
                fontSize: '11px',
                fontWeight: '600',
                color: '#8aa1ad',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                marginTop: '1px',
              }}
            >
              {activeField === 'from' ? 'Select "From" Month' : 'Select "To" Month'}
            </span>
          </div>

          <button
            type="button"
            onClick={handleNextYear}
            disabled={viewYear >= currentYear}
            style={{
              background: 'none',
              border: 'none',
              cursor: viewYear >= currentYear ? 'not-allowed' : 'pointer',
              color: viewYear >= currentYear ? '#cbd5e1' : '#8aa1ad',
              display: 'grid',
              placeItems: 'center',
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              transition: 'background 0.15s, color 0.15s',
            }}
            onMouseEnter={(e) => {
              if (viewYear < currentYear) {
                e.currentTarget.style.background = '#f1f5f9';
                e.currentTarget.style.color = '#2b3c73';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none';
              e.currentTarget.style.color = viewYear >= currentYear ? '#cbd5e1' : '#8aa1ad';
            }}
            title="Next Year"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>

        {/* 12 Months Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '10px 8px',
            padding: '14px 20px',
          }}
        >
          {MONTH_NAMES.map((name, idx) => {
            const isFuture = viewYear > currentYear || (viewYear === currentYear && idx > currentMonthIdx);
            const thisMonthMoment = moment().year(viewYear).month(idx).startOf('month');
            
            const isFrom = fromYear === viewYear && fromMonth === idx;
            const isTo = toYear === viewYear && toMonth === idx;
            const isInRange = thisMonthMoment.isSameOrAfter(rangeStart) && thisMonthMoment.isSameOrBefore(rangeEnd);

            let btnBg = 'transparent';
            let btnColor = isFuture ? '#cbd5e1' : '#0f172a';
            let btnBorder = '1.5px solid transparent';
            let btnFontWeight = '600';

            if (activeField === 'from') {
              if (isFrom) {
                btnBg = '#2b3c73';
                btnColor = '#ffffff';
                btnBorder = '1.5px solid #2b3c73';
                btnFontWeight = '700';
              } else if (isTo && !isSingle) {
                btnBg = '#ffffff';
                btnColor = '#2b3c73';
                btnBorder = '2px solid #2b3c73';
                btnFontWeight = '700';
              } else if (isInRange) {
                btnBg = '#e0e7ff';
                btnColor = '#2b3c73';
                btnFontWeight = '600';
              }
            } else {
              // activeField === 'to'
              if (isTo) {
                btnBg = '#2b3c73';
                btnColor = '#ffffff';
                btnBorder = '1.5px solid #2b3c73';
                btnFontWeight = '700';
              } else if (isFrom && !isSingle) {
                btnBg = '#ffffff';
                btnColor = '#2b3c73';
                btnBorder = '2px solid #2b3c73';
                btnFontWeight = '700';
              } else if (isInRange) {
                btnBg = '#e0e7ff';
                btnColor = '#2b3c73';
                btnFontWeight = '600';
              }
            }

            return (
              <button
                key={name}
                type="button"
                disabled={isFuture}
                onClick={() => handleMonthClick(idx)}
                style={{
                  height: '38px',
                  borderRadius: '10px',
                  border: btnBorder,
                  background: btnBg,
                  color: btnColor,
                  fontSize: '14px',
                  fontWeight: btnFontWeight,
                  cursor: isFuture ? 'not-allowed' : 'pointer',
                  transition: 'all 0.15s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onMouseEnter={(e) => {
                  if (!isFuture && !isInRange && !isFrom && !isTo) {
                    e.currentTarget.style.background = '#f1f5f9';
                    e.currentTarget.style.color = '#2b3c73';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isFuture && !isInRange && !isFrom && !isTo) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#0f172a';
                  }
                }}
              >
                {name}
              </button>
            );
          })}
        </div>

        {/* Footer Actions */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            padding: '16px 20px 20px',
            borderTop: '1px solid #f1f5f9',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1,
              height: '42px',
              borderRadius: '10px',
              border: '1px solid #d1d5db',
              background: '#ffffff',
              color: '#2b3c73',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background 0.15s, border-color 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f8fafc';
              e.currentTarget.style.borderColor = '#94a3b8';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#ffffff';
              e.currentTarget.style.borderColor = '#d1d5db';
            }}
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleConfirm}
            style={{
              flex: 1,
              height: '42px',
              borderRadius: '10px',
              border: 'none',
              background: '#F5B800',
              color: '#0b2e45',
              fontSize: '14px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(245, 184, 0, 0.35)',
              transition: 'background 0.15s, transform 0.1s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#e5ad00';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#F5B800';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}
